import React, { FC, useState, useRef, useEffect } from 'react';
import { useFormik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import { FormInput } from '../../../merchants/FormInput';
import {
	getInitialLoadingValues,
	getValidationSchema,
	initialLoadingValues,
	LOADING_FIELDS,
	LOADING_FIELDS_NAMES, LoadingFieldsTypes,
} from '../../../../constants/loading-modal/loading-form';
import {
	changeDisputeQueueParams,
	createDisputeQueue,
	fetchDisputeQueues,
	fetchReportProvidersList,
} from '../../../../store/disputes/actions/dispute-queues';
import { useDisputesContext } from '../../../../store/disputes/disputes-context';
import { ButtonComponent } from '../../../ButtonComponent';
import { FormCheckbox } from '../../../application/FormCheckbox';
import { Title } from '../../../developers/page-components/Title';
import { errorHandler } from '../../../../utils/newApplicationUtils';
import { ReportTypeDropdown } from './ReportTypeDropdown';
import { fetchWhiteListedPartnersList } from '../../../../store/merchnts/actions/partners';
import { useMerchants } from '../../../../store/merchnts/merchants-context';
import { useLocation } from 'react-router';
import { IDisputeQueuesFilters } from '../../DisputeQueuesFilterSection';
import { parseSearchParams } from '../../../../utils/utilities';
import { InputType } from '../../../form/form-input-constants';

const extensionsList = [
	{
		value: 'csv',
		label: 'CSV'
	},
	{
		value: 'exel',
		label: 'Exel'
	},
];

interface Props {
	closeModal: () => void
	queueId?: string;
}

export const NewQueueModal: FC<Props> = ({ closeModal, queueId }) => {
	const fileInputRef = useRef(null);
	const { search } = useLocation();

	const { disputesContextState, dispatch } = useDisputesContext();
	const { reportProvidersList, isReportProviderListLoading, disputeQueues } = disputesContextState;

	const { merchantState: { whitelabelPartnersListSelector, isWhitelabelPartnersListSelectorLoading }, dispatch: merchantDispatch } = useMerchants();

	const [selectedFile, setSelectedFile] = useState(null);
	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);

	const filtersFromUrl: IDisputeQueuesFilters = parseSearchParams(search) || {};

	const queueData = disputeQueues.find(item => item.id === queueId);

	const formik = useFormik<LoadingFieldsTypes>({
		initialValues: queueId ? getInitialLoadingValues(queueData) : initialLoadingValues,
		validationSchema: getValidationSchema(),
		onSubmit: () => void 0,
	});

	const typesList = reportProvidersList?.find(x => x.value === formik.getFieldProps(LOADING_FIELDS.PROVIDERS).value)?.types;

	useEffect(() => {
		void fetchReportProvidersList({ dispatch });
	}, []);

	useEffect(() => {
		void fetchWhiteListedPartnersList(merchantDispatch);
	}, []);

	useEffect(() => {
		const type = reportProvidersList?.find(x => x?.value === formik.getFieldProps(LOADING_FIELDS.PROVIDERS).value)?.types[0].value;
		formik.setFieldValue(LOADING_FIELDS.TYPE, type);
	}, [formik.values?.provider, reportProvidersList]);

	const handleFileChange = (e):void => {
		const selectedFile = e.target.files[0];
		if (selectedFile) {
			setSelectedFile(selectedFile);
		}
	};

	const handleDownload = (): void => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleDeleteFile = (): void => {
		setSelectedFile(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = ''; // Clear the input value
		}
	};

	const handleUpload = async (): Promise<void> => {
		try {
			formik.handleSubmit();
			if (!formik.isValid) return;
			setLoading(true);
			await createDisputeQueue(formik, selectedFile);
			setTimeout(async () => {
				await fetchDisputeQueues({ dispatch, searchParams: filtersFromUrl });
				closeModal();
				// await fetchDisputeQueueData(dispatch, disputesContextState, tabState?.id);
				setLoading(false);
			}, 6000);
		} catch (e) {
			setLoading(false);
			console.log('handleUpload error', e.response);
			if (e?.response?.data?.errors?.General?.[0] || typeof e?.response?.data === 'string') {
				setError(e?.response?.data?.errors?.General?.[0] || e?.response?.data);
			} else {
				errorHandler({ e, formik });
			}
		}
	};

	const handleEdit = async (): Promise<void> => {
		try {
			formik.handleSubmit();
			if (!formik.isValid) return;
			setLoading(true);
			const body = {
				name: formik.values[LOADING_FIELDS.NAME],
				emailRecipient: formik.values[LOADING_FIELDS.EMAIL],
				openItemsAlert: formik.values[LOADING_FIELDS.OPEN_ITEMS_ALERT],
				newItemsAlert: formik.values[LOADING_FIELDS.NEW_ITEMS_ALERT],
			};
			await changeDisputeQueueParams(queueId, body);
			await fetchDisputeQueues({ dispatch, searchParams: filtersFromUrl });
			closeModal();
			setLoading(false);
		} catch (e) {
			setLoading(false);

			console.log('handleEdit error', e.response);
			if (e?.response?.data?.errors?.General?.[0] || typeof e?.response?.data === 'string') {
				setError(e?.response?.data?.errors?.General?.[0] || e?.response?.data);
			} else {
				errorHandler({ e, formik });
			}
		}
	};

	return (
    <div className="px-8 mt-5 pb-5">
      <div className="row">
        <FormInput
          formik={formik}
          fieldName={LOADING_FIELDS.NAME}
          label={LOADING_FIELDS_NAMES.NAME}
          placeholder={LOADING_FIELDS_NAMES.NAME_PLACEHOLDER}
          inputType={InputType.Text}
          additionalClasses="col-md-12 mb-sm-2"
		      key={LOADING_FIELDS.NAME}
          required
        />
        <FormInput
          formik={formik}
          fieldName={LOADING_FIELDS.PARTNER_ID}
          label={LOADING_FIELDS_NAMES.PARTNER_ID}
          placeholder={LOADING_FIELDS_NAMES.PARTNER_ID}
          inputType={InputType.Select}
          searchable={false}
          cleanable={false}
          data={whitelabelPartnersListSelector}
          disabled={isWhitelabelPartnersListSelectorLoading}
          additionalClasses="col-md-12"
		      key={LOADING_FIELDS.PARTNER_ID}
        />
	      <FormInput
          formik={formik}
          fieldName={LOADING_FIELDS.PROVIDERS}
          label={LOADING_FIELDS_NAMES.PROVIDERS}
          inputType={InputType.Select}
          searchable={false}
          cleanable={false}
          data={reportProvidersList}
          fieldsToDefault={[LOADING_FIELDS.TYPE]}
          disabled={isReportProviderListLoading || !!queueId}
          additionalClasses="col-md-6"
          required
		      key={LOADING_FIELDS.PROVIDERS}
        />

				<ReportTypeDropdown formik={formik} typesList={typesList} />

        <FormInput
          formik={formik}
          fieldName={LOADING_FIELDS.EXTENSIONS}
          label={LOADING_FIELDS_NAMES.EXTENSIONS}
          inputType={InputType.Select}
          searchable={false}
          disabled={true}
          data={extensionsList}
          additionalClasses="mb-0"
          required
		      key={LOADING_FIELDS.EXTENSIONS}
        />
        <div className="col-md-4">
          <ButtonComponent
            buttonName="Select file"
            additionalClass="btn-sm mt-7"
            onButtonClick={handleDownload}
            isDisabled={!!queueId}
          />
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept='.csv, text/csv'
          />
        </div>
        <div className="col-md-5 mt-10">
          {selectedFile?.name && (
            <div className="d-flex align-items-center">
              <span className="fc-text-overflow">{selectedFile?.name}</span>
              <FontAwesomeIcon
                icon={faXmark}
                width={15}
                className={clsx('text-muted ms-2 me-3 h-15px', selectedFile?.name && 'cursor-pointer')}
                onClick={handleDeleteFile}
              />
            </div>
          )}
        </div>
      </div>

      <FormCheckbox
        formik={formik}
        fieldName={LOADING_FIELDS.AUTO_MATCH}
        label={LOADING_FIELDS_NAMES.AUTO_MATCH}
        tooltipText={LOADING_FIELDS_NAMES.AUTO_MATCH_DESCRIPTION}
        additionalClasses="col-md-6"
	      disabled={!!queueId}
      />
      <div className="fs-9 text-muted auto-match-note mt-1">
        Note: ‘Auto match’ should be checked only if the source file has 1:1 id matching!
      </div>

      <div className="separator my-5" />

      <Title title="Notification settings" additionalClass="fw-bolder fs-5" />

      <div className="separator my-5" />

      <div className="row">

        <FormCheckbox
          formik={formik}
          fieldName={LOADING_FIELDS.OPEN_ITEMS_ALERT}
          label={LOADING_FIELDS_NAMES.OPEN_ITEMS_ALERT}
          tooltipText={LOADING_FIELDS_NAMES.OPEN_ITEMS_ALERT_DESCRIPTION}
          additionalClasses="col-md-6"
        />

        <FormCheckbox
          formik={formik}
          fieldName={LOADING_FIELDS.NEW_ITEMS_ALERT}
          label={LOADING_FIELDS_NAMES.NEW_ITEMS_ALERT}
          tooltipText={LOADING_FIELDS_NAMES.NEW_ITEMS_ALERT_DESCRIPTION}
          additionalClasses="col-md-6"
        />

        <FormInput
          formik={formik}
          fieldName={LOADING_FIELDS.EMAIL}
          label={LOADING_FIELDS_NAMES.EMAIL}
          placeholder={LOADING_FIELDS_NAMES.EMAIL_PLACEHOLDER}
          inputType={InputType.Text}
          additionalClasses="col-md-6 mt-5"
		      key={LOADING_FIELDS.EMAIL}
        />
      </div>

      <div className={clsx('d-flex align-items-center', error ? 'justify-content-between' : 'justify-content-end')}>
        <div className="text-danger mt-10">{error}</div>
        <ButtonComponent
          buttonName={queueId ? 'Save' : 'Add'}
          additionalClass="btn-sm mt-7 w-100px"
          onButtonClick={queueId ? handleEdit : handleUpload}
          isDisabled={(!selectedFile && !queueId) || loading}
          loading={loading}
        />
      </div>
    </div>
  );
};
